import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeartPulse, faLungs, faPersonWalking, faFire,
  faRoute, faMoon, faStar, faBolt, faStairs,
  faBrain, faBatteryFull, faWaveSquare, faWind,
  faGaugeHigh, faWeightScale, faPercent, faDroplet,
  faSpinner, faLink, faLinkSlash, faRotate,
  faClock, faChartLine, faCheck, faXmark,
  faCircleExclamation, faPlugCircleCheck, faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Line,
} from 'recharts';
import wearableService from '../../services/wearableService';
import {
  WearableProvider,
  WearableConnection,
  WearableDataPoint,
  WearableDailySummary,
} from '../../types';
import './WearableDevices.css';
import BackButton from '../../components/BackButton/BackButton';
import { useProfile } from '../../context/ProfileContext';

// Metric display configuration
interface MetricConfig {
  label: string;
  icon: IconDefinition;
  color: string;
}

const METRIC_CONFIG: Record<string, MetricConfig> = {
  heart_rate:               { label: 'Heart Rate',        icon: faHeartPulse,     color: '#f43f5e' },
  blood_oxygen:             { label: 'Blood Oxygen',      icon: faLungs,          color: '#06b6d4' },
  steps:                    { label: 'Steps',             icon: faPersonWalking,  color: '#10b981' },
  calories:                 { label: 'Calories',          icon: faFire,           color: '#f59e0b' },
  distance:                 { label: 'Distance',          icon: faRoute,          color: '#8b5cf6' },
  sleep_duration:           { label: 'Sleep Duration',    icon: faMoon,           color: '#6366f1' },
  sleep_score:              { label: 'Sleep Score',       icon: faStar,           color: '#a855f7' },
  active_minutes:           { label: 'Active Minutes',    icon: faBolt,           color: '#ec4899' },
  floors_climbed:           { label: 'Floors Climbed',    icon: faStairs,         color: '#14b8a6' },
  stress_level:             { label: 'Stress Level',      icon: faBrain,          color: '#ef4444' },
  body_battery:             { label: 'Body Battery',      icon: faBatteryFull,    color: '#22c55e' },
  resting_heart_rate:       { label: 'Resting HR',        icon: faHeartPulse,     color: '#e11d48' },
  hrv:                      { label: 'HRV',               icon: faWaveSquare,     color: '#7c3aed' },
  respiratory_rate:         { label: 'Respiratory Rate',  icon: faWind,           color: '#0ea5e9' },
  blood_pressure_systolic:  { label: 'BP Systolic',       icon: faGaugeHigh,      color: '#f43f5e' },
  blood_pressure_diastolic: { label: 'BP Diastolic',      icon: faGaugeHigh,      color: '#f97316' },
  weight:                   { label: 'Weight',            icon: faWeightScale,    color: '#06b6d4' },
  body_fat:                 { label: 'Body Fat',          icon: faPercent,        color: '#f59e0b' },
  glucose:                  { label: 'Glucose',           icon: faDroplet,        color: '#eab308' },
};

const getMetricConfig = (metricType: string): MetricConfig => {
  return METRIC_CONFIG[metricType] || {
    label: metricType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    icon: faChartLine,
    color: '#64748b',
  };
};

const formatTimeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const formatDateTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  }) + ' ' + date.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const formatChartDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const formatMetricValue = (value: number, metricType: string): string => {
  if (metricType === 'sleep_duration') {
    const hours = Math.floor(value / 60);
    const mins = Math.round(value % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }
  if (metricType === 'distance') {
    return value >= 1000 ? `${(value / 1000).toFixed(1)}` : `${Math.round(value)}`;
  }
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1);
};


const PROVIDER_CATEGORIES: Record<string, { label: string; slugs: string[] }> = {
  popular: {
    label: 'Popular',
    slugs: ['fitbit', 'apple-health', 'google-fit', 'samsung-health', 'garmin', 'oura', 'whoop', 'strava', 'polar', 'xiaomi', 'amazfit', 'withings', 'huawei', 'suunto', 'coros'],
  },
  fitness: {
    label: 'Fitness',
    slugs: ['fitbit', 'garmin', 'strava', 'peloton', 'whoop', 'polar', 'suunto', 'coros', 'wahoo', 'zwift', 'trainerroad', 'trainingpeaks', 'mapmyfitness', 'underarmour', 'concept2', 'ifit', 'technogym', 'rouvy', 'hammerhead', 'xert', 'finalsurge', 'trainasone', 'tridot', 'virtuagym', 'komoot', 'ridewithgps', 'brytonsport', 'liverowing', 'mapmytracks', 'cyclinganalytics', 'nolio', 'velohero', 'todaysplan', 'xoss', 'lezyne', 'catapultone'],
  },
  health: {
    label: 'Health',
    slugs: ['apple-health', 'google-fit', 'samsung-health', 'health-connect', 'oura', 'withings', 'biostrap', 'elitehrv', 'omron', 'omronus', 'aktiia', 'renpho', 'inbody', 'boditrax', 'tenovi', 'healthgauge', 'moxy', 'core', 'ultrahuman', 'cardiomood'],
  },
  nutrition: {
    label: 'Nutrition',
    slugs: ['myfitnesspal', 'cronometer', 'fatsecret', 'nutracheck', 'eatthismuch', 'macrosfirst', 'ketomojous', 'ketomojoeu'],
  },
  sleep: {
    label: 'Sleep',
    slugs: ['oura', 'whoop', 'eight', 'eightsleep', 'withings', 'garmin', 'fitbit', 'polar'],
  },
  medical: {
    label: 'Medical',
    slugs: ['dexcom', 'dexcom-eu', 'dexcom-jp', 'freestyle-libre', 'omron', 'omronus', 'tenovi', 'aktiia'],
  },
  smartwatch: {
    label: 'Smartwatch',
    slugs: ['apple-health', 'samsung-health', 'google-fit', 'garmin', 'amazfit', 'xiaomi', 'huawei', 'wear-os', 'suunto', 'coros', 'polar', 'zepp'],
  },
  women: {
    label: "Women's Health",
    slugs: ['clue', 'flo'],
  },
};

const WearableDevices: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [providers, setProviders] = useState<WearableProvider[]>([]);
  const [connections, setConnections] = useState<WearableConnection[]>([]);
  const [latestData, setLatestData] = useState<WearableDataPoint[]>([]);
  const [summaries, setSummaries] = useState<WearableDailySummary[]>([]);

  const [providersLoading, setProvidersLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);

  const [connectingSlug, setConnectingSlug] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<number | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const [trendDays, setTrendDays] = useState(7);
  const [showConnectModal, setShowConnectModal] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // Check URL params for OAuth callback
  useEffect(() => {
    const connectedSlug = searchParams.get('connected');
    const error = searchParams.get('error');

    if (connectedSlug) {
      showToast('success', t('services:wearables.connectedSuccess', { name: connectedSlug.charAt(0).toUpperCase() + connectedSlug.slice(1) }));
      setSearchParams({}, { replace: true });
    } else if (error === 'oauth_failed') {
      showToast('error', t('services:wearables.oauthFailed'));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, showToast]);

  // Fetch providers and connections
  const fetchProvidersAndConnections = useCallback(async () => {
    setProvidersLoading(true);
    try {
      const [provData, connData] = await Promise.allSettled([
        wearableService.getProviders(),
        wearableService.getConnections(),
      ]);
      if (provData.status === 'fulfilled') setProviders(provData.value);
      if (connData.status === 'fulfilled') setConnections(connData.value);
    } catch (err) {
      console.error('Error fetching providers/connections:', err);
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  // Fetch latest metrics
  const fetchLatestMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const data = await wearableService.getLatestDataPoints();
      setLatestData(data);
    } catch (err) {
      console.error('Error fetching latest data:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  // Fetch daily summaries
  const fetchSummaries = useCallback(async (days: number) => {
    setTrendsLoading(true);
    try {
      const data = await wearableService.getDailySummaries(days);
      setSummaries(data);
    } catch (err) {
      console.error('Error fetching summaries:', err);
    } finally {
      setTrendsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProvidersAndConnections();
    fetchLatestMetrics();
  }, [fetchProvidersAndConnections, fetchLatestMetrics, activeProfile]);

  useEffect(() => {
    fetchSummaries(trendDays);
  }, [trendDays, fetchSummaries, activeProfile]);

  // Connection helpers
  const getConnectionForProvider = (providerSlug: string): WearableConnection | undefined => {
    return connections.find(c => c.provider.slug === providerSlug);
  };

  const filteredProviders = providers.filter(p => {
    const matchesSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (activeCategory === 'all') return true;
    if (activeCategory === 'my-devices') {
      return connections.some(c => c.status === 'connected' && c.provider.slug === p.slug);
    }
    return PROVIDER_CATEGORIES[activeCategory]?.slugs.includes(p.slug) ?? false;
  });

  const connectedProviders = providers
    .map(provider => {
      const connection = getConnectionForProvider(provider.slug);
      return connection?.status === 'connected' ? { provider, connection } : null;
    })
    .filter(Boolean) as { provider: WearableProvider; connection: WearableConnection }[];

  const connectedCount = connections.filter(c => c.status === 'connected').length;
  const totalMetrics = latestData.length;

  // Actions
  const handleConnect = async (slug: string) => {
    setConnectingSlug(slug);
    try {
      const result = await wearableService.connectProvider(slug);
      if (result.auth_url) {
        window.open(result.auth_url, '_blank', 'noopener,noreferrer');
      } else {
        showToast('error', t('services:wearables.failedToConnect'));
      }
    } catch (err: any) {
      console.error('Connect error:', err);
      const backendMessage = err?.response?.data?.detail;
      showToast('error', backendMessage || t('services:wearables.failedToConnect'));
    } finally {
      setConnectingSlug(null);
    }
  };

  const handleDisconnect = async (connectionId: number) => {
    setDisconnectingId(connectionId);
    try {
      await wearableService.disconnectProvider(connectionId);
      showToast('success', t('services:wearables.disconnectedSuccess'));
      fetchProvidersAndConnections();
      fetchLatestMetrics();
    } catch (err) {
      console.error('Disconnect error:', err);
      showToast('error', t('services:wearables.failedToDisconnect'));
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleSync = async (connectionId: number) => {
    setSyncingId(connectionId);
    try {
      await wearableService.triggerSync(connectionId);
      showToast('success', t('services:wearables.syncInitiated'));
      setTimeout(() => {
        fetchLatestMetrics();
        fetchSummaries(trendDays);
      }, 2000);
    } catch (err) {
      console.error('Sync error:', err);
      showToast('error', t('services:wearables.failedToSync'));
    } finally {
      setSyncingId(null);
    }
  };

  // Group summaries by metric type for charts
  const groupedSummaries: Record<string, WearableDailySummary[]> = {};
  summaries.forEach(s => {
    if (!groupedSummaries[s.metric_type]) groupedSummaries[s.metric_type] = [];
    groupedSummaries[s.metric_type].push(s);
  });

  // Sort each group by date
  Object.values(groupedSummaries).forEach(arr => {
    arr.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  });

  return (
    <div className="wd-page">

      {/* Toast Notification */}
      {toast && (
        <div className={`wd-toast ${toast.type}`}>
          <FontAwesomeIcon
            icon={toast.type === 'success' ? faCheck : faCircleExclamation}
            className="wd-toast-icon"
          />
          <span>{toast.message}</span>
          <button className="wd-toast-close" onClick={() => setToast(null)}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {/* ============ HERO BANNER ============ */}
      <section className="wd-hero-banner">
        <Container>
          <BackButton />
          <div className="wd-hero-layout">
            <div className="wd-hero-text">
              <h1 className="wd-hero-title">
                <FontAwesomeIcon icon={faClock} /> {t('services:wearables.title')}
              </h1>
              <p className="wd-hero-subtitle">
                {t('services:wearables.subtitle')}
              </p>
            </div>
            <div className="wd-hero-stats">
              <div className="wd-hero-stat">
                <span className="wd-hero-stat-value">{connectedCount}</span>
                <span className="wd-hero-stat-label">{t('services:wearables.connected')}</span>
              </div>
              <div className="wd-hero-stat">
                <span className="wd-hero-stat-value">{totalMetrics}</span>
                <span className="wd-hero-stat-label">{t('services:wearables.metrics')}</span>
              </div>
              <div className="wd-hero-stat">
                <span className="wd-hero-stat-value">{Object.keys(groupedSummaries).length}</span>
                <span className="wd-hero-stat-label">{t('services:wearables.trends')}</span>
              </div>
            </div>
            <button className="wd-hero-connect-btn" onClick={() => setShowConnectModal(true)}>
              <FontAwesomeIcon icon={faLink} />
              {t('services:wearables.connectDevice')}
            </button>
          </div>
        </Container>
      </section>

      {/* ============ SECTION 1: LIVE METRICS ============ */}
      <section className="wd-section" style={{ background: '#ffffff' }}>
        <Container>
          <div className="wd-section-header">
            <div>
              <h2 className="wd-section-title">
                <FontAwesomeIcon icon={faHeartPulse} className="wd-section-title-icon" />
                {t('services:wearables.liveMetrics')}
              </h2>
              <p className="wd-section-subtitle">
                {t('services:wearables.latestReadings')}
              </p>
            </div>
          </div>

          {metricsLoading ? (
            <div className="wd-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>{t('services:wearables.loadingMetrics')}</span>
            </div>
          ) : latestData.length === 0 ? (
            <div className="wd-empty-state">
              <FontAwesomeIcon icon={faHeartPulse} className="wd-empty-icon" />
              <h3>{t('services:wearables.noMetricsAvailable')}</h3>
              <p>{t('services:wearables.noMetricsDesc')}</p>
            </div>
          ) : (
            <div className="wd-metrics-grid">
              {latestData.map((point, idx) => {
                const config = getMetricConfig(point.metric_type);
                return (
                  <div
                    key={point.id}
                    className="wd-metric-card"
                    style={{ animationDelay: `${idx * 0.04}s` }}
                  >
                    <div className="wd-metric-card-top">
                      <div
                        className="wd-metric-icon"
                        style={{ background: `${config.color}12`, color: config.color }}
                      >
                        <FontAwesomeIcon icon={config.icon} />
                      </div>
                      <span className="wd-metric-provider">{point.provider_name}</span>
                    </div>
                    <div className="wd-metric-value-row">
                      <span className="wd-metric-value">
                        {formatMetricValue(point.value, point.metric_type)}
                      </span>
                      <span className="wd-metric-unit">{point.unit}</span>
                    </div>
                    <p className="wd-metric-label">{config.label}</p>
                    <span className="wd-metric-time">
                      <FontAwesomeIcon icon={faClock} />
                      {formatDateTime(point.recorded_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Container>
      </section>

      {/* ============ SECTION 3: TRENDS ============ */}
      <section className="wd-section">
        <Container>
          <div className="wd-section-header">
            <div>
              <h2 className="wd-section-title">
                <FontAwesomeIcon icon={faChartLine} className="wd-section-title-icon" />
                {t('services:wearables.healthTrends')}
              </h2>
              <p className="wd-section-subtitle">
                {t('services:wearables.dailyAverages')}
              </p>
            </div>
            <div className="wd-trends-controls">
              {[7, 14, 30].map(days => (
                <button
                  key={days}
                  className={`wd-period-btn${trendDays === days ? ' active' : ''}`}
                  onClick={() => setTrendDays(days)}
                >
                  {days}D
                </button>
              ))}
            </div>
          </div>

          {trendsLoading ? (
            <div className="wd-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>{t('services:wearables.loadingTrends')}</span>
            </div>
          ) : Object.keys(groupedSummaries).length === 0 ? (
            <div className="wd-empty-state">
              <FontAwesomeIcon icon={faChartLine} className="wd-empty-icon" />
              <h3>{t('services:wearables.noTrendData')}</h3>
              <p>{t('services:wearables.noTrendDesc')}</p>
            </div>
          ) : (
            <div className="wd-charts-grid">
              {Object.entries(groupedSummaries).map(([metricType, data], idx) => {
                const config = getMetricConfig(metricType);
                const chartData = data.map(d => ({
                  date: formatChartDate(d.date),
                  avg: d.avg_value,
                  min: d.min_value,
                  max: d.max_value,
                  total: d.total_value,
                }));

                const unit = data[0]?.unit || '';
                const useTotal = ['steps', 'calories', 'distance', 'active_minutes', 'floors_climbed'].includes(metricType);

                return (
                  <div
                    key={metricType}
                    className="wd-chart-card"
                    style={{ animationDelay: `${idx * 0.06}s` }}
                  >
                    <div className="wd-chart-header">
                      <h3 className="wd-chart-title">
                        <span
                          className="wd-chart-title-icon"
                          style={{ background: `${config.color}12`, color: config.color }}
                        >
                          <FontAwesomeIcon icon={config.icon} />
                        </span>
                        {config.label}
                      </h3>
                      <span className="wd-chart-unit">{unit}</span>
                    </div>
                    <div className="wd-chart-body">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                          <defs>
                            <linearGradient id={`gradient-${metricType}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={config.color} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={config.color} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            axisLine={{ stroke: '#e2e8f0' }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              background: '#ffffff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '10px',
                              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                              fontSize: '0.82rem',
                              fontFamily: 'Nunito, sans-serif',
                            }}
                            labelStyle={{ fontWeight: 700, color: '#1e293b', marginBottom: 4 }}
                          />
                          {!useTotal && (
                            <Area
                              type="monotone"
                              dataKey="min"
                              stroke="none"
                              fill={`url(#gradient-${metricType})`}
                              fillOpacity={1}
                              name="Min"
                            />
                          )}
                          {!useTotal && (
                            <Area
                              type="monotone"
                              dataKey="max"
                              stroke={config.color}
                              strokeOpacity={0.2}
                              strokeWidth={1}
                              fill={`url(#gradient-${metricType})`}
                              fillOpacity={0.5}
                              name="Max"
                            />
                          )}
                          {useTotal ? (
                            <Area
                              type="monotone"
                              dataKey="total"
                              stroke={config.color}
                              strokeWidth={2.5}
                              fill={`url(#gradient-${metricType})`}
                              fillOpacity={1}
                              name="Total"
                              dot={{ fill: config.color, r: 3, strokeWidth: 0 }}
                              activeDot={{ fill: config.color, r: 5, strokeWidth: 2, stroke: '#ffffff' }}
                            />
                          ) : (
                            <Line
                              type="monotone"
                              dataKey="avg"
                              stroke={config.color}
                              strokeWidth={2.5}
                              name="Average"
                              dot={{ fill: config.color, r: 3, strokeWidth: 0 }}
                              activeDot={{ fill: config.color, r: 5, strokeWidth: 2, stroke: '#ffffff' }}
                            />
                          )}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Container>
      </section>

      {/* ============ CONNECT DEVICE MODAL ============ */}
      {showConnectModal && (
        <div className="wd-modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div className="wd-modal-container" onClick={e => e.stopPropagation()}>
            <div className="wd-modal-header">
              <div>
                <h2 className="wd-modal-title">
                  <FontAwesomeIcon icon={faPlugCircleCheck} className="wd-modal-title-icon" />
                  {t('services:wearables.connectDeviceModal')}
                </h2>
                <p className="wd-modal-subtitle">{t('services:wearables.linkDevicesDesc')}</p>
              </div>
              <button className="wd-modal-close" onClick={() => setShowConnectModal(false)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="wd-modal-body">
              {providersLoading ? (
                <div className="wd-loading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>{t('services:wearables.loadingDevices')}</span>
                </div>
              ) : providers.length === 0 ? (
                <div className="wd-empty-state">
                  <FontAwesomeIcon icon={faPlugCircleCheck} className="wd-empty-icon" />
                  <h3>{t('services:wearables.noDevicesAvailable')}</h3>
                  <p>{t('services:wearables.noDevicesDesc')}</p>
                </div>
              ) : (
                <>
                  <div className="wd-filter-bar">
                    <div className="wd-search-box">
                      <FontAwesomeIcon icon={faSearch} className="wd-search-icon" />
                      <input
                        type="text"
                        className="wd-search-input"
                        placeholder={t('services:wearables.searchDevices')}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button className="wd-search-clear" onClick={() => setSearchQuery('')}>
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      )}
                    </div>
                    <div className="wd-category-tabs">
                      {connectedProviders.length > 0 && (
                        <button
                          className={`wd-category-tab${activeCategory === 'my-devices' ? ' active' : ''}`}
                          onClick={() => setActiveCategory('my-devices')}
                        >
                          {t('services:wearables.myDevices')} ({connectedProviders.length})
                        </button>
                      )}
                      <button
                        className={`wd-category-tab${activeCategory === 'all' ? ' active' : ''}`}
                        onClick={() => setActiveCategory('all')}
                      >
                        {t('services:wearables.all')} ({providers.length})
                      </button>
                      {Object.entries(PROVIDER_CATEGORIES).map(([key, cat]) => {
                        const count = providers.filter(p => cat.slugs.includes(p.slug)).length;
                        return (
                          <button
                            key={key}
                            className={`wd-category-tab${activeCategory === key ? ' active' : ''}`}
                            onClick={() => setActiveCategory(key)}
                          >
                            {cat.label} ({count})
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="wd-providers-grid">
                    {filteredProviders.map(provider => {
                      const connection = getConnectionForProvider(provider.slug);
                      const isConnected = connection?.status === 'connected';
                      const status = connection?.status || 'disconnected';

                      return (
                        <div
                          key={provider.id}
                          className={`wd-provider-card${isConnected ? ' connected' : ''}`}
                        >
                          <div className="wd-provider-header">
                            {provider.logo_url ? (
                              <img
                                src={provider.logo_url}
                                alt={provider.name}
                                className="wd-provider-logo"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  const parent = (e.target as HTMLImageElement).parentElement;
                                  if (parent) {
                                    const placeholder = document.createElement('div');
                                    placeholder.className = 'wd-provider-logo-placeholder';
                                    placeholder.textContent = provider.name.charAt(0);
                                    parent.insertBefore(placeholder, e.target as HTMLImageElement);
                                  }
                                }}
                              />
                            ) : (
                              <div className="wd-provider-logo-placeholder">
                                {provider.name.charAt(0)}
                              </div>
                            )}
                            <div className="wd-provider-info">
                              <h3 className="wd-provider-name">{provider.name}</h3>
                            </div>
                          </div>

                          <div className="wd-provider-status">
                            <span className={`wd-status-badge ${status}`}>
                              <span className={`wd-status-dot ${status}`} />
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </span>
                            {connection?.last_sync_at && (
                              <span className="wd-last-sync">
                                <FontAwesomeIcon icon={faClock} />
                                {formatTimeAgo(connection.last_sync_at)}
                              </span>
                            )}
                          </div>

                          {connection?.sync_error_message && connection.status === 'error' && (
                            <p className="wd-sync-error">
                              <FontAwesomeIcon icon={faCircleExclamation} /> {connection.sync_error_message}
                            </p>
                          )}

                          <div className="wd-provider-actions">
                            {isConnected ? (
                              <>
                                <button
                                  className="wd-connect-btn disconnect"
                                  onClick={() => handleDisconnect(connection!.id)}
                                  disabled={disconnectingId === connection!.id}
                                >
                                  <FontAwesomeIcon icon={disconnectingId === connection!.id ? faSpinner : faLinkSlash} spin={disconnectingId === connection!.id} />
                                  {disconnectingId === connection!.id ? t('services:wearables.disconnecting') : t('services:wearables.disconnect')}
                                </button>
                                <button
                                  className={`wd-sync-btn${syncingId === connection!.id ? ' syncing' : ''}`}
                                  onClick={() => handleSync(connection!.id)}
                                  disabled={syncingId === connection!.id}
                                >
                                  <FontAwesomeIcon icon={faRotate} />
                                  {syncingId === connection!.id ? t('services:wearables.syncing') : t('services:wearables.sync')}
                                </button>
                              </>
                            ) : (
                              <button
                                className="wd-connect-btn connect"
                                onClick={() => handleConnect(provider.slug)}
                                disabled={connectingSlug === provider.slug}
                              >
                                <FontAwesomeIcon icon={connectingSlug === provider.slug ? faSpinner : faLink} spin={connectingSlug === provider.slug} />
                                {connectingSlug === provider.slug ? t('services:wearables.connecting') : t('services:wearables.connect')}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {filteredProviders.length === 0 && !providersLoading && providers.length > 0 && (
                    <div className="wd-empty-state">
                      <FontAwesomeIcon icon={faSearch} className="wd-empty-icon" />
                      <h3>{t('services:wearables.noDevicesFound')}</h3>
                      <p>{t('services:wearables.noDevicesFoundDesc')}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WearableDevices;
